// ============================================================================
// "Start from" — the text a Direct note starts with
// ============================================================================
// Options: this note's current text (Guided → Direct, nothing lost), another
// engagement's note, the previous year of this client, or blank. Another
// engagement's text is COPIED (never linked) and loads unsaved for review;
// its field values re-fill from this engagement. The source may be a
// generated note (answers applied as they read there) or one still on the
// old free-edit format.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { SyutSelect } from 'components/ui';
import { fsaEngagementService } from 'api/services/fsa/fsaEngagementService';
import { type CopiedFrom, type PortableSection } from './directMode';
import { loadNoteSections, priorEngagementOf } from './priorSections';

export type StartFromResult =
  | { kind: 'current' }
  | { kind: 'blank' }
  | { kind: 'copy'; portable: PortableSection[]; from: CopiedFrom };

interface Props {
  open: boolean;
  engagementId: string;
  /** The note being filled, matched by schedule code in the source engagement. */
  scheduleCode: string;
  noteTitle: string;
  /** Guided notes offer "this note's current text"; a Direct note already has it. */
  offerCurrent: boolean;
  onCancel: () => void;
  onConfirm: (result: StartFromResult) => void;
}

type Choice = 'current' | 'engagement' | 'prior' | 'blank';

interface EngagementOption {
  id: string;
  code?: string | null;
  client?: string | null;
  yearEnd?: string | null;
}

const fy = (iso?: string | null) => (iso ? `FY end ${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');

/** Plain text of a TipTap doc (preview only). */
function docText(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const n = node as { type?: string; text?: string; content?: unknown[]; attrs?: { label?: string } };
  if (n.type === 'text') return n.text ?? '';
  if (n.type === 'fieldChip') return `[${n.attrs?.label ?? 'field'}]`;
  if (n.type === 'dataTable') return ' [table] ';
  return (n.content ?? []).map(docText).join(n.type === 'doc' ? ' ' : '');
}

const StartFromDialog: React.FC<Props> = ({ open, engagementId, scheduleCode, noteTitle, offerCurrent, onCancel, onConfirm }) => {
  const [choice, setChoice] = useState<Choice>(offerCurrent ? 'current' : 'engagement');
  const [prior, setPrior] = useState<EngagementOption | null>(null);
  const [picked, setPicked] = useState<EngagementOption | null>(null);
  const [optionsById, setOptionsById] = useState<Record<string, EngagementOption>>({});
  const [source, setSource] = useState<{ portable: PortableSection[]; saved: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setChoice(offerCurrent ? 'current' : 'engagement');
    setPicked(null);
    setSource(null);
    setError(null);
    priorEngagementOf(engagementId)
      .then(p => setPrior(p ? { id: p.id, code: p.code } : null))
      .catch(() => setPrior(null));
  }, [open, engagementId, offerCurrent]);

  const sourceEngagement = choice === 'prior' ? prior : choice === 'engagement' ? picked : null;

  // Load (and preview) the source note whenever the source engagement changes.
  useEffect(() => {
    if (!open || !sourceEngagement) { setSource(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadNoteSections(sourceEngagement.id, scheduleCode)
      .then(s => { if (!cancelled) setSource(s); })
      .catch((e: any) => { if (!cancelled) { setSource(null); setError(e?.response?.data?.message ?? e?.message ?? 'The note could not be loaded.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Reload only when the source ENGAGEMENT changes, not on every render's new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceEngagement?.id, scheduleCode]);

  const loadEngagementOptions = useCallback(async (search: string) => {
    const resp = await fsaEngagementService.getAll({ page: 1, pageSize: 30, searchTerm: search || undefined });
    const found: Record<string, EngagementOption> = {};
    const options = (resp.data ?? [])
      .filter(e => String(e.primaEngagementId) !== String(engagementId))
      .map(e => {
        const id = String(e.primaEngagementId);
        found[id] = { id, code: e.engagementCode, client: e.clientName, yearEnd: e.financialYearEnd };
        return { value: id, label: `${e.engagementCode ?? ''} · ${e.clientName ?? ''} · ${fy(e.financialYearEnd)}` };
      });
    setOptionsById(prev => ({ ...prev, ...found }));
    return options;
  }, [engagementId]);

  const visible = useMemo(() => (source?.portable ?? []).filter(p => !p.hidden), [source]);
  const canConfirm = choice === 'current' || choice === 'blank' || (!!sourceEngagement && !!source && !loading);

  const confirm = () => {
    if (choice === 'current') return onConfirm({ kind: 'current' });
    if (choice === 'blank') return onConfirm({ kind: 'blank' });
    if (!sourceEngagement || !source) return;
    onConfirm({
      kind: 'copy',
      portable: source.portable,
      from: {
        engagement_id: sourceEngagement.id,
        engagement_code: sourceEngagement.code ?? null,
        client_name: sourceEngagement.client ?? null,
        financial_year_end: sourceEngagement.yearEnd ?? null,
      },
    });
  };

  if (!open) return null;
  return createPortal(
    <div style={overlayStyle} onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={dialogStyle} role="dialog" aria-modal="true" aria-label={`Start the ${noteTitle} from`}>
        <div style={headerStyle}>
          <strong>Edit directly — start the {noteTitle} from</strong>
          <button type="button" onClick={onCancel} style={closeStyle} aria-label="Close">×</button>
        </div>
        <div style={bodyStyle}>
          <div style={hintStyle}>
            In Direct mode the note is edited as plain text and the note questions are not used. The text you pick is copied
            into this engagement — it is not linked, and nothing is saved until you press Save. Figures and tables inside the
            text fill from this engagement.
          </div>

          {offerCurrent && (
            <label style={optionStyle}>
              <input type="radio" checked={choice === 'current'} onChange={() => setChoice('current')} />
              <span><strong>This note's current text</strong> — exactly what it says now, with the answers applied. Nothing is lost.</span>
            </label>
          )}
          <label style={optionStyle}>
            <input type="radio" checked={choice === 'engagement'} onChange={() => setChoice('engagement')} />
            <span style={{ flex: 1 }}>
              <strong>Another engagement</strong> — any client, any year
              {choice === 'engagement' && (
                <div style={{ marginTop: 6 }} onClick={e => e.preventDefault()}>
                  <SyutSelect
                    value={picked?.id ?? ''}
                    onChange={v => setPicked(v ? (optionsById[String(v)] ?? { id: String(v) }) : null)}
                    isAsync
                    loadOptions={loadEngagementOptions}
                    defaultOptions
                    cacheOptions={false}
                    placeholder="Search engagement code or client…"
                    size="sm"
                    menuPortalTarget={document.body}
                  />
                </div>
              )}
            </span>
          </label>
          <label style={{ ...optionStyle, opacity: prior ? 1 : 0.5 }}>
            <input type="radio" checked={choice === 'prior'} disabled={!prior} onChange={() => setChoice('prior')} />
            <span>
              <strong>Previous year of this client</strong>
              {prior ? ` — ${prior.code ?? 'previous engagement'}` : ' — no previous-year engagement is linked'}
            </span>
          </label>
          <label style={optionStyle}>
            <input type="radio" checked={choice === 'blank'} onChange={() => setChoice('blank')} />
            <span><strong>Blank</strong> — start with no sections and add your own.</span>
          </label>

          {sourceEngagement && (
            <div style={previewStyle}>
              {loading && <div style={hintStyle}>Loading the note…</div>}
              {error && <div style={{ ...hintStyle, color: '#b91c1c' }}>{error}</div>}
              {source && !loading && (
                <>
                  <div style={{ ...hintStyle, marginBottom: 6 }}>
                    {visible.length} section{visible.length === 1 ? '' : 's'} will be copied
                    {source.saved ? '' : ' — that note has not been saved yet, so this is its default text'}:
                  </div>
                  {visible.map((p, i) => (
                    <div key={i} style={previewRowStyle}>
                      <div style={{ fontWeight: 600 }}>{p.title}</div>
                      <div style={previewTextStyle}>{docText(p.content).trim().slice(0, 160) || '(empty)'}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        <div style={footerStyle}>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={confirm} disabled={!canConfirm}>Use this text</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Above the note modal (z 10050), below portaled select menus (10200) so the
// engagement picker's menu opens on top of this dialog.
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 10150,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const dialogStyle: React.CSSProperties = {
  width: 'min(720px, 94vw)', maxHeight: '86vh', display: 'flex', flexDirection: 'column',
  background: 'var(--bg-primary, #fff)', borderRadius: 10, boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
};
const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
  borderBottom: '1px solid var(--border-color)', fontSize: '0.95rem', color: 'var(--text-primary)',
};
const closeStyle: React.CSSProperties = { border: 'none', background: 'transparent', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--text-secondary)' };
const bodyStyle: React.CSSProperties = { padding: '12px 16px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 };
const footerStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border-color)' };
const hintStyle: React.CSSProperties = { fontSize: '0.78rem', color: 'var(--text-secondary)' };
const optionStyle: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.85rem', color: 'var(--text-primary)', cursor: 'pointer' };
const previewStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)', borderRadius: 6, padding: '8px 10px', background: 'var(--bg-secondary)', maxHeight: 280, overflow: 'auto',
};
const previewRowStyle: React.CSSProperties = { padding: '4px 0', borderTop: '1px solid var(--border-color)', fontSize: '0.8rem', color: 'var(--text-primary)' };
const previewTextStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-secondary)' };

export default StartFromDialog;
